/**
 * VerifyEmailBanner — "Confirma tu correo" para el cliente Free (freemium,
 * 29-sep-2026). Verificación suave: entra y usa todo al instante, pero con el
 * correo sin confirmar no descarga su certificado ni recibe la secuencia de
 * seguimiento. Miembros, gurus, admin y cuentas de Google no lo ven.
 */
import { MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useUser } from '@/lib/auth';
import { useMembership } from '@/lib/hooks/useMembership';
import { useEmailVerified, useResendVerification } from '@/lib/api/email';

export function VerifyEmailBanner() {
  const user = useUser();
  const { isFreeClient } = useMembership();
  const { data: verified, isLoading } = useEmailVerified();
  const resend = useResendVerification();

  if (!isFreeClient || isLoading || verified !== false) return null;

  async function onResend() {
    try {
      const r = await resend.mutateAsync();
      if (r === 'sent') toast.success('Te enviamos el enlace. Revisa tu bandeja (y la de spam).');
      else if (r === 'too_soon') toast.info('Ya te lo enviamos hace un momento. Revisa tu bandeja o intenta en 5 minutos.');
      else if (r === 'limit') toast.info('Ya enviamos varios enlaces hoy. Revisa tu bandeja o intenta mañana.');
      else toast.success('Tu correo ya está confirmado.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo enviar el correo');
    }
  }

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-brand-primary/20 bg-brand-primary/[0.05] p-4 md:flex-row md:items-center">
      <MailCheck className="h-5 w-5 flex-shrink-0 text-brand-primary" />
      <div className="flex-1 text-sm">
        <p className="font-semibold text-brand-text-strong">
          Confirma tu correo{user?.email ? ` (${user.email})` : ''}
        </p>
        <p className="mt-0.5 text-muted-foreground">
          Te enviamos un enlace al registrarte. Con tu correo confirmado aseguras tu certificado.
        </p>
      </div>
      <Button variant="secondary" size="sm" onClick={onResend} loading={resend.isPending}>
        Reenviar correo
      </Button>
    </div>
  );
}
